/**
 * Price Calculator Component
 *
 * Displays price breakdown and total
 */

import { motion } from 'framer-motion';
import type { ProductConfiguration } from './types';

interface PriceCalculatorProps {
  configuration: ProductConfiguration;
  totalPrice: number;
}

export const PriceCalculator = ({ configuration, totalPrice }: PriceCalculatorProps) => {
  const { selectedVariant, selectedAddOns } = configuration;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 space-y-4"
    >
      <h3 className="text-lg font-semibold text-gray-900">Price Breakdown</h3>

      <div className="space-y-2">
        {/* Base Product */}
        {selectedVariant ? (
          <div className="flex justify-between text-gray-700">
            <span>
              {configuration.baseProduct.name} - {selectedVariant.color}
            </span>
            <span className="font-medium">CAD ${selectedVariant.price.toFixed(2)}</span>
          </div>
        ) : (
          <div className="text-gray-500 italic">Select a color to see pricing</div>
        )}

        {/* Add-ons */}
        {selectedAddOns.length > 0 && (
          <>
            <div className="border-t border-gray-300 pt-2 mt-2" />
            {selectedAddOns.map((addon) => (
              <div key={addon.id} className="flex justify-between text-gray-700">
                <span>{addon.name}</span>
                <span className="font-medium">
                  {addon.price === 0 ? (
                    <span className="text-green-600">Included</span>
                  ) : (
                    `+CAD $${addon.price.toFixed(2)}`
                  )}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Total */}
      {selectedVariant && (
        <>
          <div className="border-t-2 border-gray-300 pt-4" />
          <div className="flex justify-between items-center">
            <span className="text-xl font-bold text-gray-900">Total</span>
            <span className="text-2xl font-bold text-brand-primary">
              CAD ${totalPrice.toFixed(2)}
            </span>
          </div>
        </>
      )}
    </motion.div>
  );
};
